/**
 * Shared eligibility helpers for Account → Subscriptions actions.
 */

import { buildSlotPatternRows, WEEKDAY_COLUMNS } from '../../utils/subscriptionDisplay';

const DAY_INDEX = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 0,
};

/** Customer-facing meal slots. Mid-morning folds into breakfast; evening snack into dinner. */
export const CUSTOMER_DELIVERY_SLOTS = [
  { code: 'breakfast', label: 'Breakfast', shortLabel: 'Brk' },
  { code: 'lunch', label: 'Lunch', shortLabel: 'Lunch' },
  { code: 'dinner', label: 'Dinner', shortLabel: 'Din' },
];

const SLOT_FAMILIES = {
  breakfast: ['breakfast', 'mid_morning'],
  lunch: ['lunch'],
  dinner: ['dinner', 'evening_snack'],
};

export function isSubscriptionPaid(sub) {
  if (!sub) return false;
  if (sub.isPaid === true) return true;
  const paidAmt = Number(sub.amountPaid);
  return Number.isFinite(paidAmt) && paidAmt > 0;
}

/** Approved + paid — required for meal-slot holds and full-plan pause. */
export function isApprovedPaidSubscription(sub) {
  if (!sub) return false;
  return String(sub.orderStatus || '').toUpperCase() === 'APPROVED' && isSubscriptionPaid(sub);
}

export function isPausedSubscription(sub) {
  return String(sub?.orderStatus || '').toUpperCase() === 'PAUSED';
}

export function todayIsoLocal() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addUtcDays(iso, days) {
  const match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return iso;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  date.setUTCDate(date.getUTCDate() + Number(days || 0));
  return date.toISOString().slice(0, 10);
}

export function weekdayFromIso(iso) {
  const match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const index = date.getUTCDay();
  return WEEKDAY_COLUMNS.find((col) => DAY_INDEX[col.key] === index)?.key || '';
}

function normalizeSlotCode(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_');
}

/** Breakfast+mid-morning and dinner+evening-snack are one customer slot each. */
export function canonicalizeDeliverySlotCode(value) {
  const code = normalizeSlotCode(value);
  if (!code || code === 'full_day') return code;
  if (code === 'mid_morning' || code === 'midmorning' || code === 'mid_mor_snack') {
    return 'breakfast';
  }
  if (code === 'evening_snack' || code === 'evening' || code === 'evng_snack' || code === 'snacks' || code === 'snack') {
    return 'dinner';
  }
  return code;
}

export function deliverySlotLabel(code, fallback) {
  const canonical = canonicalizeDeliverySlotCode(code) || normalizeSlotCode(code);
  const match = CUSTOMER_DELIVERY_SLOTS.find((slot) => slot.code === canonical);
  if (match) return match.label;
  if (fallback && fallback !== code) return fallback;
  return String(canonical || code || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function deliverySlotShortLabel(code) {
  const canonical = canonicalizeDeliverySlotCode(code) || normalizeSlotCode(code);
  const match = CUSTOMER_DELIVERY_SLOTS.find((slot) => slot.code === canonical);
  return match?.shortLabel || deliverySlotLabel(canonical);
}

function slotLabelFromCode(code, fallback) {
  return deliverySlotLabel(code, fallback);
}

function mergeWeekdays(current, incoming) {
  const order = WEEKDAY_COLUMNS.map((col) => col.key);
  const set = new Set([...(current || []), ...(incoming || [])]);
  return order.filter((day) => set.has(day));
}

export function resolvePurchasedInternalSlotCodes(sub) {
  const snap = sub?.webPlanSnapshot || {};
  const codes = [
    ...(Array.isArray(sub?.purchasedSlots) ? sub.purchasedSlots : []),
    ...(Array.isArray(snap.slotCodes) ? snap.slotCodes : []),
    ...(Array.isArray(snap.mealSlots) ? snap.mealSlots : []),
    ...(Array.isArray(snap.uiSlotCodes) ? snap.uiSlotCodes : []),
  ].map(normalizeSlotCode).filter(Boolean);
  const unique = [...new Set(codes)];
  if (unique.includes('full_day')) {
    return [...new Set([
      ...unique,
      'breakfast',
      'mid_morning',
      'lunch',
      'evening_snack',
      'dinner',
    ])];
  }
  return unique;
}

/** Pause breakfast also covers mid-morning; pause dinner also covers evening snack. */
export function expandCanonicalSlotCodes(canonicalCode, purchasedCodes) {
  const canonical = canonicalizeDeliverySlotCode(canonicalCode) || normalizeSlotCode(canonicalCode);
  if (!canonical || canonical === 'full_day') {
    return CUSTOMER_DELIVERY_SLOTS.flatMap((slot) => (
      expandCanonicalSlotCodes(slot.code, purchasedCodes)
    ));
  }
  const purchased = new Set((purchasedCodes || []).map(normalizeSlotCode).filter(Boolean));
  const family = SLOT_FAMILIES[canonical] || [canonical];
  if (!purchased.size) return [canonical];
  const hits = [];
  const allowFamily = purchased.has('full_day');
  family.forEach((code) => {
    if (allowFamily || purchased.has(code)) hits.push(code);
  });
  purchased.forEach((code) => {
    if (code === 'full_day') return;
    if (canonicalizeDeliverySlotCode(code) === canonical && !hits.includes(code)) {
      hits.push(code);
    }
  });
  return hits.length ? hits : [canonical];
}

export function groupSubscriptionHolds(holds) {
  const groups = [];
  const index = new Map();
  (holds || []).forEach((hold) => {
    const slotCode = canonicalizeDeliverySlotCode(hold.slotCode) || normalizeSlotCode(hold.slotCode);
    const originalDate = String(hold.originalDate || '').slice(0, 10);
    const resumeDate = hold.resumeDate ? String(hold.resumeDate).slice(0, 10) : '';
    const key = `${originalDate}|${slotCode}|${hold.status}|${hold.holdType}|${resumeDate}`;
    if (!index.has(key)) {
      const row = {
        id: hold.id,
        holdIds: [hold.id],
        originalDate,
        slotCode,
        slotLabel: deliverySlotLabel(slotCode, hold.slotCode),
        holdType: hold.holdType,
        status: hold.status,
        resumeDate: resumeDate || null,
      };
      index.set(key, row);
      groups.push(row);
    } else {
      index.get(key).holdIds.push(hold.id);
    }
  });
  return groups;
}

function datesForWeekdays(startIso, endIso, weekdays) {
  const start = String(startIso || '').slice(0, 10);
  const end = String(endIso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end) || start > end) {
    return [];
  }
  const allowed = new Set(
    (weekdays || [])
      .map((day) => DAY_INDEX[day])
      .filter((index) => Number.isInteger(index)),
  );
  if (!allowed.size) return [];
  const dates = [];
  const cursor = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);
  while (cursor <= last) {
    if (allowed.has(cursor.getUTCDay())) dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

function pushSlotOption(expanded, code, label, days) {
  const normalized = normalizeSlotCode(code);
  if (!normalized) return;
  const weekdays = Array.isArray(days) && days.length
    ? days
    : WEEKDAY_COLUMNS.map((col) => col.key);
  if (normalized === 'full_day') {
    CUSTOMER_DELIVERY_SLOTS.forEach((slot) => {
      pushSlotOption(expanded, slot.code, slot.label, weekdays);
    });
    return;
  }
  const canonical = canonicalizeDeliverySlotCode(normalized) || normalized;
  const existing = expanded.find((row) => row.code === canonical);
  if (existing) {
    existing.days = mergeWeekdays(existing.days, weekdays);
    return;
  }
  expanded.push({
    code: canonical,
    label: slotLabelFromCode(canonical, label),
    shortLabel: deliverySlotShortLabel(canonical),
    days: weekdays,
  });
}

function sortSlotOptions(expanded) {
  const order = CUSTOMER_DELIVERY_SLOTS.map((slot) => slot.code);
  return expanded
    .filter((row) => row.code)
    .sort((a, b) => (
      (order.indexOf(a.code) === -1 ? 99 : order.indexOf(a.code))
      - (order.indexOf(b.code) === -1 ? 99 : order.indexOf(b.code))
    ));
}

export function resolveSubscriptionSlotOptions(sub) {
  const expanded = [];
  const patternRows = buildSlotPatternRows(sub || {});
  patternRows.forEach((row) => {
    pushSlotOption(expanded, row.slotCode || row.slotLabel, row.slotLabel, row.days);
  });
  if (expanded.length) return sortSlotOptions(expanded);

  const snap = sub?.webPlanSnapshot || {};
  const codes = Array.isArray(sub?.purchasedSlots) && sub.purchasedSlots.length
    ? sub.purchasedSlots
    : (Array.isArray(snap.slotCodes)
      ? snap.slotCodes
      : (Array.isArray(snap.mealSlots) ? snap.mealSlots : []));
  const labels = Array.isArray(snap.slotLabels) ? snap.slotLabels : [];
  const planDays = Array.isArray(snap.days) && snap.days.length
    ? snap.days
    : WEEKDAY_COLUMNS.map((col) => col.key);
  (codes || []).forEach((code, index) => {
    pushSlotOption(expanded, code, labels[index], planDays);
  });
  return sortSlotOptions(expanded);
}

export function nextMatchingDate(afterIso, weekdays) {
  const allowed = weekdays?.length ? weekdays : WEEKDAY_COLUMNS.map((col) => col.key);
  let cursor = addUtcDays(afterIso, 1);
  for (let i = 0; i < 180; i += 1) {
    if (allowed.includes(weekdayFromIso(cursor))) return cursor;
    cursor = addUtcDays(cursor, 1);
  }
  return addUtcDays(afterIso, 1);
}

function weekdaysForPausedGroup(items, slotOptions) {
  const sets = (items || []).map((item) => {
    const code = canonicalizeDeliverySlotCode(item.slotCode) || item.slotCode;
    const slot = (slotOptions || []).find((option) => option.code === code);
    return slot?.days?.length ? slot.days : WEEKDAY_COLUMNS.map((col) => col.key);
  });
  if (!sets.length) return WEEKDAY_COLUMNS.map((col) => col.key);
  const shared = sets[0].filter((day) => sets.every((days) => days.includes(day)));
  return shared.length ? shared : sets[0];
}

export function projectToDateAfterPause(currentEnd, pausedSlots, slotOptions) {
  const groups = new Map();
  (pausedSlots || []).forEach((item, index) => {
    const date = String(item.date || '').slice(0, 10);
    const key = date || `anon-${index}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });
  let end = currentEnd;
  Array.from(groups.keys()).sort().forEach((key) => {
    end = nextMatchingDate(end, weekdaysForPausedGroup(groups.get(key), slotOptions));
  });
  return end;
}

/** Original plan end from the builder snapshot (not the extended makeup toDate). */
export function resolvePatternRangeEnd(sub, fallbackToDate) {
  const snapEnd = String(sub?.webPlanSnapshot?.endDate || '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(snapEnd)) return snapEnd;
  const fallback = String(fallbackToDate || sub?.toDate || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(fallback) ? fallback : '';
}

/** Inclusive ISO date list from fromDate..toDate (UTC calendar days). */
export function listSubscriptionCalendarDays(sub) {
  const from = String(sub?.fromDate || '').slice(0, 10);
  const to = String(sub?.toDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return [];
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const days = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

/**
 * Upcoming purchased meal-slot occurrences (date + slot), future days only.
 * Pattern days stop at the original plan end. Scheduled makeup dates are added
 * explicitly so a far makeup day does not fill the gap as regular deliveries.
 * @returns {{ id: string, date: string, slotCode: string, slotLabel: string, isMakeup?: boolean }[]}
 */
export function listFutureSlotOccurrences(sub, { today, toDate, holds } = {}) {
  const todayIso = today || todayIsoLocal();
  const rangeStart = String(sub?.fromDate || '').slice(0, 10);
  const currentEnd = String(toDate || sub?.toDate || '').slice(0, 10);
  const patternEnd = resolvePatternRangeEnd(sub, currentEnd);
  const minDate = rangeStart && rangeStart > todayIso ? rangeStart : todayIso;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(minDate)) return [];

  const slotOptions = resolveSubscriptionSlotOptions(sub);
  const occurrences = [];
  const seen = new Set();
  const pushOccurrence = (date, slot, isMakeup = false) => {
    if (!date || date < todayIso) return;
    const id = `${date}::${slot.code}`;
    if (seen.has(id)) return;
    seen.add(id);
    occurrences.push({
      id,
      date,
      slotCode: slot.code,
      slotLabel: slot.label,
      isMakeup,
    });
  };

  if (/^\d{4}-\d{2}-\d{2}$/.test(patternEnd) && patternEnd >= minDate) {
    slotOptions.forEach((slot) => {
      datesForWeekdays(minDate, patternEnd, slot.days).forEach((date) => {
        pushOccurrence(date, slot, false);
      });
    });
  }

  (Array.isArray(holds) ? holds : []).forEach((hold) => {
    if (String(hold?.status || '').toLowerCase() !== 'scheduled') return;
    const date = String(hold.resumeDate || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    const raw = String(hold.slotCode || '').trim();
    const code = canonicalizeDeliverySlotCode(raw) || normalizeSlotCode(raw);
    const slot = slotOptions.find((option) => option.code === code)
      || { code, label: slotLabelFromCode(code), shortLabel: deliverySlotShortLabel(code) };
    pushOccurrence(date, slot, Boolean(patternEnd && date > patternEnd));
  });

  const order = CUSTOMER_DELIVERY_SLOTS.map((slot) => slot.code);
  return occurrences.sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return (order.indexOf(a.slotCode) === -1 ? 99 : order.indexOf(a.slotCode))
      - (order.indexOf(b.slotCode) === -1 ? 99 : order.indexOf(b.slotCode));
  });
}
